/**
 * Cash & Bank over time and against the bank: cash flow by day, week, month
 * or year; bank statement checkpoints; and the journal behind every balance.
 *
 * None of these record money. A statement is the bank's closing balance set
 * beside CenoX's — a difference is shown, never booked — and the journal is
 * every movement already recorded, as balanced entries.
 */

import { useState, type ReactNode } from 'react';
import { CASH_FLOW_GRANULARITIES, type CashFlowGranularity, type MoneyAccountView } from '@cenox/shared';
import { Badge, Button, Card, ErrorState, Input, Modal, Select, TableSkeleton, cn } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { money } from '../../lib/format';
import { dayLabel, SOURCE_LABELS } from './labels';
import { useCashFlow, useJournal, useRecordStatement, useStatements, useVoidStatement } from './data';

const GRANULARITY_LABELS: Record<CashFlowGranularity, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' };

export function CashFlowCard({
  from,
  to,
  area,
  currency,
}: {
  from: string;
  to: string;
  area: Record<string, string | undefined>;
  currency: string;
}): ReactNode {
  const [granularity, setGranularity] = useState<CashFlowGranularity>('week');
  const flow = useCashFlow({ from, to, granularity, ...area });
  const m = (v: string) => money(v, currency);
  // Transfers between the company's own accounts only show when the view leaves one side out.
  const showTransfers = (flow.data?.rows ?? []).some((r) => r.transfers !== '0.00');
  const unexplained = (flow.data?.rows ?? []).filter((r) => r.difference !== '0.00');
  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Cash flow</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Money in and out of the company (own transfers excluded), income received, expenses paid, and the cash and bank held
            and what was lent out at the end of each — counted as the Total Cash and Total Bank panels count them, money not in an
            account yet included. Each row&apos;s closing is the row before it plus what moved.
          </p>
        </div>
        <Select
          label="Step"
          value={granularity}
          onChange={(e) => setGranularity(e.target.value as CashFlowGranularity)}
          options={CASH_FLOW_GRANULARITIES.map((g) => ({ value: g, label: GRANULARITY_LABELS[g] }))}
        />
      </div>
      {flow.isError ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {flow.error instanceof ApiError ? flow.error.message : 'Could not load the cash flow.'}
        </p>
      ) : !flow.data ? (
        <TableSkeleton rows={3} columns={8} />
      ) : (
        <div className="-mx-3 mt-2 overflow-x-auto px-3">
          <table className="w-full min-w-[52rem] text-sm" data-testid="cash-flow">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1 pr-3 font-medium">Period</th>
                <th className="py-1 pr-3 text-right font-medium">In</th>
                <th className="py-1 pr-3 text-right font-medium">Out</th>
                <th className="py-1 pr-3 text-right font-medium">Net</th>
                <th className="py-1 pr-3 text-right font-medium">Income</th>
                <th className="py-1 pr-3 text-right font-medium">Expenses</th>
                {showTransfers ? <th className="py-1 pr-3 text-right font-medium">Own transfers</th> : null}
                <th className="py-1 pr-3 text-right font-medium">Cash (closing)</th>
                <th className="py-1 pr-3 text-right font-medium">Bank (closing)</th>
                <th className="py-1 text-right font-medium">Loans out</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr className="text-slate-600" data-testid="cash-flow-opening">
                <td className="whitespace-nowrap py-1.5 pr-3 text-xs">Opening — as of {dayLabel(flow.data.opening.asOf)}</td>
                <td colSpan={showTransfers ? 6 : 5} />
                <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(flow.data.opening.cash)}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(flow.data.opening.bank)}</td>
                <td />
              </tr>
              {flow.data.rows.map((r) => (
                <tr key={r.from}>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-xs text-slate-600">{r.from === r.to ? r.from : `${r.from} – ${r.to}`}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-emerald-700">{m(r.moneyIn)}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-rose-700">{m(r.moneyOut)}</td>
                  <td className={cn('whitespace-nowrap py-1.5 pr-3 text-right tabular-nums', Number(r.net) < 0 ? 'text-rose-700' : '')}>{m(r.net)}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(r.income)}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(r.expenses)}</td>
                  {showTransfers ? <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(r.transfers)}</td> : null}
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(r.cash)}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{m(r.bank)}</td>
                  <td className="whitespace-nowrap py-1.5 text-right tabular-nums">{m(r.loansOutstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {unexplained.length > 0 ? (
            <p role="alert" className="mt-2 text-xs text-rose-700" data-testid="cash-flow-difference">
              {unexplained.map((r) => `${r.from === r.to ? r.from : `${r.from} – ${r.to}`}: ${m(r.difference)}`).join('; ')} not
              explained by the movements in {unexplained.length === 1 ? 'that row' : 'those rows'}. The balances are each worked out
              from the records; please report this so it can be looked into.
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export function StatementsCard({
  accounts,
  currency,
  mayManage,
  today,
}: {
  accounts: MoneyAccountView[];
  currency: string;
  mayManage: boolean;
  today: string;
}): ReactNode {
  const statements = useStatements();
  const record = useRecordStatement();
  const voidStatement = useVoidStatement();
  const banks = accounts.filter((a) => a.kind !== 'cash' && !a.archived);
  const [adding, setAdding] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [statementOn, setStatementOn] = useState(today);
  const [closingBalance, setClosing] = useState('');
  const [reference, setReference] = useState('');
  const [voiding, setVoiding] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const list = statements.data ?? [];
  if (banks.length === 0 && list.length === 0) return null;
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Bank statements</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            The closing balance on the bank&apos;s own statement, beside CenoX&apos;s. A difference is shown to be explained —
            nothing is booked to make them agree.
          </p>
        </div>
        {mayManage && banks.length > 0 && !adding ? (
          <Button size="sm" variant="secondary" onClick={() => { setAdding(true); setAccountId(banks[0]!.id); }}>
            Check a statement
          </Button>
        ) : null}
      </div>
      {adding ? (
        <form
          className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5 lg:items-end"
          aria-label="Check a statement"
          onSubmit={(e) => {
            e.preventDefault();
            record.mutate(
              { accountId, statementOn, closingBalance, reference: reference.trim() },
              { onSuccess: () => { setAdding(false); setClosing(''); setReference(''); } },
            );
          }}
        >
          <Select label="Account" value={accountId} onChange={(e) => setAccountId(e.target.value)} options={banks.map((b) => ({ value: b.id, label: b.name }))} />
          <Input label="Statement date" type="date" max={today} value={statementOn} onChange={(e) => setStatementOn(e.target.value)} />
          <Input label="Closing balance" inputMode="decimal" value={closingBalance} onChange={(e) => setClosing(e.target.value)} />
          <Input label="Statement reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
            <Button type="submit" size="sm" loading={record.isPending} disabled={!closingBalance || !reference.trim()}>Check</Button>
          </div>
          {record.error ? <p role="alert" className="text-sm text-red-700 sm:col-span-2 lg:col-span-5">{record.error.message}</p> : null}
        </form>
      ) : null}
      {statements.isError ? (
        <ErrorState message="Could not load statements." onRetry={() => void statements.refetch()} />
      ) : list.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">No statement checked yet.</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100" data-testid="statements">
          {list.map((s) => (
            <li key={s.id} className="py-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className={s.voided ? 'text-slate-400 line-through' : undefined}>
                  <p className="text-sm font-medium">
                    {s.accountName} · {s.statementOn} · ref {s.reference}{' '}
                    {s.voided ? null : s.reconciled ? <Badge tone="success">Agrees</Badge> : <Badge tone="warning">Differs</Badge>}
                  </p>
                  <p className="text-xs text-slate-500">
                    Bank {money(s.closingBalance, currency)} · CenoX {money(s.bookBalance, currency)}
                    {s.reconciled ? '' : ` · difference ${money(s.difference, currency)}`}
                    {s.inTransit !== '0.00' ? ` · ${money(s.inTransit, currency)} of deposits still on their way that day` : ''}
                    {s.recordedBy ? ` · by ${s.recordedBy}` : ''}
                  </p>
                </div>
                {s.voided ? (
                  <span className="text-xs text-red-700">Voided: {s.voidReason}</span>
                ) : mayManage && voiding !== s.id ? (
                  <Button variant="ghost" size="sm" onClick={() => { setVoiding(s.id); setReason(''); }}>Void</Button>
                ) : null}
              </div>
              {voiding === s.id ? (
                <form
                  className="mt-2 flex items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    voidStatement.mutate({ id: s.id, reason }, { onSuccess: () => setVoiding(null) });
                  }}
                >
                  <div className="flex-1"><Input label="Why is it void?" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setVoiding(null)}>Cancel</Button>
                  <Button type="submit" variant="danger" size="sm" loading={voidStatement.isPending} disabled={reason.trim().length < 3}>Void</Button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function JournalModal({
  filters,
  currency,
  onClose,
}: {
  filters: { from: string; to: string } & Record<string, string | undefined>;
  currency: string;
  onClose: () => void;
}): ReactNode {
  const journal = useJournal(
    { from: filters.from, to: filters.to, ...(filters.accountId ? { accountId: filters.accountId } : {}), ...(filters.branchId ? { branchId: filters.branchId } : {}) },
    true,
  );
  const m = (v: string) => (v === '0.00' ? '' : money(v, currency));
  const j = journal.data;
  return (
    <Modal open onClose={onClose} title={`Journal ${filters.from} to ${filters.to}`} size="lg">
      {journal.isError ? (
        <p role="alert" className="text-sm text-red-700">Could not load the journal.</p>
      ) : !j ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm">
            Debits {money(j.totals.debit, currency)} · credits {money(j.totals.credit, currency)}{' '}
            {j.totals.balanced ? <Badge tone="success">Balanced</Badge> : <Badge tone="danger">Out of balance</Badge>}
          </p>
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Trial balance</h3>
            <table className="w-full text-sm" data-testid="trial-balance">
              <tbody className="divide-y divide-slate-100">
                {j.trialBalance.map((t) => (
                  <tr key={t.account}>
                    <td className="py-1 pr-3">{t.accountName}</td>
                    <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">{m(t.debit)}</td>
                    <td className="whitespace-nowrap py-1 text-right tabular-nums">{m(t.credit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="max-h-[45vh] overflow-y-auto">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Entries{j.truncated ? ` (first ${j.entries.length})` : ''}
            </h3>
            <table className="w-full text-sm" data-testid="journal-entries">
              <tbody>
                {j.entries.map((e, i) =>
                  e.lines.map((l, k) => (
                    <tr key={`${i}-${k}`} className={k === 0 ? 'border-t border-slate-200' : undefined}>
                      <td className="whitespace-nowrap py-0.5 pr-3 text-xs text-slate-500">{k === 0 ? e.on : ''}</td>
                      <td className="py-0.5 pr-3 text-xs text-slate-500">{k === 0 ? (SOURCE_LABELS[e.source] ?? e.source) : ''}</td>
                      <td className={cn('py-0.5 pr-3', Number(l.credit) > 0 ? 'pl-4' : '')}>{l.accountName}</td>
                      <td className="whitespace-nowrap py-0.5 pr-3 text-right tabular-nums">{m(l.debit)}</td>
                      <td className="whitespace-nowrap py-0.5 text-right tabular-nums">{m(l.credit)}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
